import React from 'react';
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import acordLogo from 'images/acordlogo.png';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import { useProducers, fetchAgentContact, fetchPolicyAgentContact } from '../ProducerSection';
import InsurerSearchDropdown, { resolveCarrierIdByName } from '../InsurerSearchDropdown';
import { useSelectedClient } from '../../../../hooks/useSelectedClient';
import { mapFields, str } from '../acordFieldEngine';
import type { AcordFieldItem } from '../acordFieldEngine';
import { COVERAGE_ROW_MAPS, POLICY_MAP } from './acord27FieldMap';
import type { Acord27FormData } from '../../utils/acord27Payload';
import { parseAcord27StoredCoverages } from '../../utils/parseAcord27StoredCoverages';
import type { StoredCoverage, StoredPolicy } from '../../utils/parseAcord27StoredCoverages';

// Parses ISO date strings (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS) using local timezone
// to avoid the UTC-midnight shift that causes dates to display one day behind.
function parseLocalDate(v: string): Date {
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(v);
}

// Previous approach: show all 10 coverage rows
// const COVERAGE_ROW_COUNT = 10;

// BIE-match approach: show only Building (A) + Business Personal Property/Contents (B)
const COVERAGE_ROW_COUNT = 10;

interface Acord27FormProps {
  showAcord101?: boolean;
  selectedPolicies?: string[];
  insuredId?: string;
  selectedLocationLabel?: string;
  editCoverages?: StoredCoverage[];
  editPolicies?: StoredPolicy[];
  isV2016?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export interface Acord27FormRef {
  getFormData(): Acord27FormData;
  applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>): void;
}

const Acord27Form = React.forwardRef<Acord27FormRef, Acord27FormProps>(
function Acord27Form({ showAcord101 = false, selectedPolicies = [], insuredId = '', selectedLocationLabel = '', editCoverages, editPolicies, isV2016 = false, onLoadingChange }: Acord27FormProps, ref) {
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [agencyAddress1, setAgencyAddress1] = React.useState('');
  const [agencyAddress2, setAgencyAddress2] = React.useState('');
  const [agencyAddress3, setAgencyAddress3] = React.useState('');
  const { client } = useSelectedClient();
  const [agencyCity, setAgencyCity] = React.useState('');
  const [agencyState, setAgencyState] = React.useState('');
  const [agencyZip, setAgencyZip] = React.useState('');
  const [agencyPhone, setAgencyPhone] = React.useState('');
  const [agencyFax, setAgencyFax] = React.useState('');
  const [agencyEmail, setAgencyEmail] = React.useState('');
  const [agencyCode, setAgencyCode] = React.useState('');
  const [agencySubCode, setAgencySubCode] = React.useState('');
  const [agencyCustomer, setAgencyCustomer] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [companyId, setCompanyId] = React.useState<string | null>(null);
  const [loanNumber, setLoanNumber] = React.useState('');
  const [policyNumber, setPolicyNumber] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState<Date | null>(null);
  const [expirationDate, setExpirationDate] = React.useState<Date | null>(null);
  const [continuedUntilTerminated, setContinuedUntilTerminated] = React.useState(true);
  const [replacesPriorDate, setReplacesPriorDate] = React.useState<Date | null>(null);
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress1, setInsuredAddress1] = React.useState('');
  const [insuredAddress2, setInsuredAddress2] = React.useState('');
  const [insuredAddress3, setInsuredAddress3] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');
  const [locationDescription, setLocationDescription] = React.useState('');

  // v2016 perils fields
  const [perilsInsured, setPerilsInsured] = React.useState(false);
  const [basic, setBasic] = React.useState(false);
  const [broad, setBroad] = React.useState(false);
  const [special, setSpecial] = React.useState(true);
  const [otherCoverageCheck, setOtherCoverageCheck] = React.useState(false);
  const [otherCoverageText, setOtherCoverageText] = React.useState('');

  const [coverageRows, setCoverageRows] = React.useState(
    Array.from({ length: COVERAGE_ROW_COUNT }, () => ({
      coveragePerilsForms: '',
      amountOfInsurance: '',
      deductible: '',
    }))
  );

  const updateCoverageRow = (index: number, field: string, value: string) => {
    setCoverageRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  // Pre-fill insured fields and agency contact info from selected client
  React.useEffect(() => {
    if (!client) return;
    setInsuredName(client.name || '');
    setInsuredAddress1(client.name || '');
    setInsuredAddress2(client.addressLine1 || '');
    setInsuredCity(client.city || '');
    setInsuredState(client.state || '');
    setInsuredZip(client.zip || '');
    setAcord101NamedInsured(client.name || '');
  }, [client]);

  // ACORD 101
  const [acord101Agency, setAcord101Agency] = React.useState('');
  const [acord101NamedInsured, setAcord101NamedInsured] = React.useState('');
  const [acord101AdditionalRemarks, setAcord101AdditionalRemarks] = React.useState('');

  // Remarks
  const [remarks, setRemarks] = React.useState('');

  const [aiEnabled, setAiEnabled] = React.useState(false);
  const [additionalInterestName, setAdditionalInterestName] = React.useState('');
  const [aiMortgagee, setAiMortgagee] = React.useState(false);
  const [aiAdditionalInsured, setAiAdditionalInsured] = React.useState(false);
  const [aiLossPayee, setAiLossPayee] = React.useState(false);
  const [aiLenderLossPayable, setAiLenderLossPayable] = React.useState(false);
  const [aiOtherCheck, setAiOtherCheck] = React.useState(false);
  const [aiOtherText, setAiOtherText] = React.useState('');
  const [aiLoanNumber, setAiLoanNumber] = React.useState('');
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);

  const editPrefillPending = React.useRef(!!(editCoverages && editCoverages.length > 0));

  // Prefill location description from the selected location in the General section
  React.useEffect(() => {
    if (editPrefillPending.current) return;
    setLocationDescription(selectedLocationLabel);
  }, [selectedLocationLabel]);


  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;

    // Edit mode: restore from stored XML on first trigger
    if (editPrefillPending.current) {
      editPrefillPending.current = false;
      if (editCoverages && editCoverages.length > 0) {
        const p = parseAcord27StoredCoverages(editCoverages, editPolicies || []);
        if (p.certDate)       setCertDate(p.certDate);
        setAgencyAddress1(p.agencyAddress1);
        setAgencyAddress2(p.agencyAddress2);
        setAgencyAddress3(p.agencyAddress3);
        setAgencyCity(p.agencyCity);
        setAgencyState(p.agencyState);
        setAgencyZip(p.agencyZip);
        setAgencyPhone(formatUsPhone(p.agencyPhone));
        setAgencyFax(formatUsPhone(p.agencyFax));
        setAgencyEmail(p.agencyEmail);
        setAgencyCode(p.agencyCode);
        setAgencySubCode(p.agencySubCode);
        setAgencyCustomer(p.agencyCustomer);
        setCompany(p.company);
        // Resolve carrier databaseId for NaicId in edit mode
        if (p.company) {
          resolveCarrierIdByName(p.company).then(id => { if (id) setCompanyId(id); });
        }
        setLoanNumber(p.loanNumber);
        setPolicyNumber(p.policyNumber);
        if (p.effectiveDate)  setEffectiveDate(p.effectiveDate);
        if (p.expirationDate) setExpirationDate(p.expirationDate);
        setContinuedUntilTerminated(p.continuedUntilTerminated);
        if (p.replacesPriorDate) setReplacesPriorDate(p.replacesPriorDate);
        setInsuredAddress1(p.insuredAddress1);
        setInsuredAddress2(p.insuredAddress2);
        setInsuredAddress3(p.insuredAddress3);
        setInsuredCity(p.insuredCity);
        setInsuredState(p.insuredState);
        setInsuredZip(p.insuredZip);
        setLocationDescription(p.locationDescription);
        setPerilsInsured(p.perilsInsured);
        setBasic(p.basic);
        setBroad(p.broad);
        setSpecial(p.special);
        setOtherCoverageCheck(p.otherCoverageCheck);
        setOtherCoverageText(p.otherCoverageText);
        setCoverageRows(p.coverageRows);
        setRemarks(p.remarks);
        if (p.additionalInterestName || p.aiMortgagee || p.aiAdditionalInsured || p.aiLossPayee || p.aiLenderLossPayable || p.aiOtherCheck || p.aiLoanNumber) {
          setAiEnabled(true);
        }
        setAdditionalInterestName(p.additionalInterestName);
        setAiMortgagee(p.aiMortgagee);
        setAiAdditionalInsured(p.aiAdditionalInsured);
        setAiLossPayee(p.aiLossPayee);
        setAiLenderLossPayable(p.aiLenderLossPayable);
        setAiOtherCheck(p.aiOtherCheck);
        setAiOtherText(p.aiOtherText);
        setAiLoanNumber(p.aiLoanNumber);
        if (p.authRepSignature) setAuthRepSignature(p.authRepSignature);
        if (p.acord101AdditionalRemarks) setAcord101AdditionalRemarks(p.acord101AdditionalRemarks);
      }
      onLoadingChange?.(false);
      return;
    }

    let cancelled = false;
    const fetchAll = async () => {
      onLoadingChange?.(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';

        const [coveragesRes, policyRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId: 'E30E94D8-F099-44CD-B7A9-EE34353B50DA',
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }),
          fetch(
            `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCoi/policy-details-byid`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'NCToken': ncToken,
                Authorization: `Bearer ${igToken}`,
              },
              body: JSON.stringify({ databaseIds: selectedPolicies }),
            }
          ),
        ]);

        if (cancelled) return;

        const coveragesJson = await coveragesRes.json();
        const arr: AcordFieldItem[] = Array.isArray(coveragesJson.d)
          ? coveragesJson.d
          : Array.isArray(coveragesJson.Data)
          ? coveragesJson.Data
          : [];
        let covPolicyNumber = '';
        let covEffectiveDate: Date | null = null;
        let covExpirationDate: Date | null = null;

        if (arr.length > 0) {
          const ALL_LETTERS = ['A','B','C','D','E','F','G','H','I','J'];
          const allSlots = ALL_LETTERS.map(l => mapFields(arr, {
            coveragePerilsForms: str(`EvidenceOfProperty_CoverageDescription_${l}`),
            amountOfInsurance:   str(`EvidenceOfProperty_LimitAmount_${l}`),
            deductible:          str(`EvidenceOfProperty_DeductibleAmount_${l}`),
          }));

          const empty = { coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };

          const buildingSlot = allSlots.find(s => {
            const raw = (s.coveragePerilsForms || '').toUpperCase();
            return raw.includes('BUILDING') || raw.includes('BLDG');
          });

          const contentsSlot = allSlots.find(s => {
            const raw = (s.coveragePerilsForms || '').toUpperCase();
            return raw.includes('CONTENT') || raw.includes('BUS PERS PROP');
          });

          const buildingRow  = { ...( buildingSlot  ?? empty ), coveragePerilsForms: buildingSlot  ? 'Building'  : '' };
          const contentsRow  = { ...( contentsSlot  ?? empty ), coveragePerilsForms: contentsSlot  ? 'Business Personal Property'  : '' };

          if (!isV2016) {
            setCoverageRows(prev => prev.map((row, i) => {
              if (i === 0) return buildingRow as typeof row;
              if (i === 1) return contentsRow as typeof row;
              return { ...row, coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };
            }));
          }

          const policy = mapFields(arr, POLICY_MAP);
          covPolicyNumber   = policy.policyNumber   || '';
          covEffectiveDate  = policy.effectiveDate  || null;
          covExpirationDate = policy.expirationDate || null;
          if (covPolicyNumber)   setPolicyNumber(covPolicyNumber);
          if (covEffectiveDate)  setEffectiveDate(covEffectiveDate);
          if (covExpirationDate) setExpirationDate(covExpirationDate);
        }

        if (policyRes.ok) {
          const policyJson = await policyRes.json();
          const detail = Array.isArray(policyJson.value) ? policyJson.value[0] : null;
          if (detail) {
            if (!covPolicyNumber   && detail.number)         setPolicyNumber(detail.number);
            if (!covEffectiveDate  && detail.effectiveDate)  setEffectiveDate(parseLocalDate(detail.effectiveDate));
            if (!covExpirationDate && detail.expirationDate) setExpirationDate(parseLocalDate(detail.expirationDate));
            if (detail.carrierName) {
              setCompany(detail.carrierName);
              // Resolve carrier databaseId for NaicId
              resolveCarrierIdByName(detail.carrierName).then(id => { if (id) setCompanyId(id); });
            }
          }
        }

        // Prefill agent contact fields from policy agent API
        const agentContact = await fetchPolicyAgentContact(selectedPolicies[0]);
        if (cancelled) return;
        if (agentContact.phone) setAgencyPhone(formatUsPhone(agentContact.phone));
        if (agentContact.fax)   setAgencyFax(formatUsPhone(agentContact.fax));
        if (agentContact.email) setAgencyEmail(agentContact.email);
        if (agentContact.agentId && agentContact.name) {
          const { npnNumber, addressLine1, city, state, zipCode } = await fetchAgentContact(agentContact.agentId);
          setAgencyAddress1(npnNumber ? `${agentContact.name} (${npnNumber})` : agentContact.name);
          if (addressLine1) setAgencyAddress2(addressLine1);
          if (city) setAgencyCity(city);
          if (state) setAgencyState(state);
          if (zipCode) setAgencyZip(zipCode);
          const agencyParts = [agentContact.name, addressLine1, city, state, zipCode].filter(Boolean);
          setAcord101Agency(agencyParts.join(', '));
        }
      } catch {
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; onLoadingChange?.(false); };
  }, [selectedPolicies, insuredId]);

  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) return;
    fetchPolicyAgentContact(selectedPolicies[0]).then(async agentContact => {
      if (agentContact.agentId && agentContact.name) {
        const { addressLine1, city, state, zipCode } = await fetchAgentContact(agentContact.agentId);
        const agencyParts = [agentContact.name, addressLine1, city, state, zipCode].filter(Boolean);
        setAcord101Agency(agencyParts.join(', '));
      }
    });
  }, [selectedPolicies]);

  const prefillPropertyFromBuildingCoverages = (returnObjects: Array<{ fieldName: string; value: string }>) => {
    const get = (name: string) => returnObjects.find(o => o.fieldName === name)?.value ?? '';
    const getBool = (name: string) => get(name) === 'Yes';

    setPerilsInsured(getBool('PerilsInsured'));
    setBasic(getBool('Basic'));
    setBroad(getBool('Broad'));
    setSpecial(true);
    setOtherCoverageCheck(getBool('OtherCoverage'));
    setRemarks(get('Remarks'));

    const letters = ['A','B','C','D','E','F','G','H','I','J'] as const;
    const empty = { coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };

    const allSlots = letters.map(l => ({
      coveragePerilsForms: get(`EvidenceOfProperty_CoverageDescription_${l}`),
      amountOfInsurance:   get(`EvidenceOfProperty_LimitAmount_${l}`),
      deductible:          get(`EvidenceOfProperty_DeductibleAmount_${l}`),
    }));

    const buildingSlot = allSlots.find(s => {
      const raw = (s.coveragePerilsForms || '').toUpperCase();
      return raw.includes('BUILDING') || raw.includes('BLDG');
    });

    const contentsSlot = allSlots.find(s => {
      const raw = (s.coveragePerilsForms || '').toUpperCase();
      return raw.includes('CONTENT') || raw.includes('BUS PERS PROP');
    });

    const buildingRow = { ...(buildingSlot ?? empty), coveragePerilsForms: buildingSlot ? 'Building' : '' };
    const contentsRow = { ...(contentsSlot ?? empty), coveragePerilsForms: contentsSlot ? 'Business Personal Property' : '' };

    setCoverageRows(prev => prev.map((row, i) => {
      if (buildingSlot) {
        if (i === 0) return buildingRow as typeof row;
        if (i === 1) return contentsRow as typeof row;
      } else {
        if (i === 0) return contentsRow as typeof row;
      }
      return { ...row, coveragePerilsForms: '', amountOfInsurance: '', deductible: '' };
    }));
  };

  React.useImperativeHandle(ref, () => ({
    applyBuildingCoverages(returnObjects: Array<{ fieldName: string; value: string }>) {
      prefillPropertyFromBuildingCoverages(returnObjects);
    },
    getFormData: (): Acord27FormData => ({
      certDate,
      agencyAddress1,
      agencyAddress2,
      agencyAddress3,
      agencyCity,
      agencyState,
      agencyZip,
      agencyPhone,
      agencyFax,
      agencyEmail,
      agencyCode,
      agencySubCode,
      agencyCustomer,
      company,
      companyId,
      loanNumber,
      policyNumber,
      effectiveDate,
      expirationDate,
      continuedUntilTerminated,
      replacesPriorDate,
      insuredAddress1,
      insuredAddress2,
      insuredAddress3,
      insuredCity,
      insuredState,
      insuredZip,
      locationDescription,
      perilsInsured,
      basic,
      broad,
      special,
      otherCoverageCheck,
      otherCoverageText,
      coverageRows,
      remarks,
      additionalInterestName,
      aiMortgagee,
      aiAdditionalInsured,
      aiLossPayee,
      aiLenderLossPayable,
      aiOtherCheck,
      aiOtherText,
      aiLoanNumber,
      authRepSignature,
      showAcord101,
      acord101Agency,
      acord101NamedInsured,
      acord101AdditionalRemarks,
    }),
  }));

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">EVIDENCE OF PROPERTY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This evidence of property insurance is issued as a matter of information only and confers no rights upon the additional interest named below. This evidence of property insurance does not amend, extend or alter the coverage afforded by the policies below.
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__sectionHeader">AGENCY:</div>
              <InputText maxLength={100} value={agencyAddress1} onChange={(e) => setAgencyAddress1(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={agencyAddress2} onChange={(e) => setAgencyAddress2(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={agencyAddress3} onChange={(e) => setAgencyAddress3(e.target.value)} placeholder="Suite / Unit" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={agencyCity} onChange={(e) => setAgencyCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={agencyState} onChange={(e) => setAgencyState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={agencyZip} onChange={(e) => setAgencyZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div style={{ marginBottom: 6 }}>
              <div className="masterCert__contactLabel">PHONE:</div>
              <InputText maxLength={20} value={agencyPhone} onChange={(e) => setAgencyPhone(maskUsPhone(e.target.value))} className="masterCert__input masterCert__inputFull" />
            </div>

            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">FAX:</div>
                <InputText maxLength={20} value={agencyFax} onChange={(e) => setAgencyFax(maskUsPhone(e.target.value))} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">E-MAIL:</div>
                <InputText maxLength={100} value={agencyEmail} onChange={(e) => setAgencyEmail(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">CODE:</div>
                <InputText maxLength={30} value={agencyCode} onChange={(e) => setAgencyCode(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">SUB CODE:</div>
                <InputText maxLength={30} value={agencySubCode} onChange={(e) => setAgencySubCode(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div>
              <div className="masterCert__contactLabel">AGENCY CUSTOMER:</div>
              <InputText maxLength={30} value={agencyCustomer} onChange={(e) => setAgencyCustomer(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>COMPANY</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <InsurerSearchDropdown value={company} onChange={setCompany} onIdChange={setCompanyId} className="masterCert__input masterCert__inputFull" />
            </div>

            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">LOAN NUMBER</div>
                <InputText maxLength={30} value={loanNumber} onChange={(e) => setLoanNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
              <div>
                <div className="masterCert__contactLabel">POLICY NUMBER</div>
                <InputText maxLength={50} value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div className="masterCert__contactGrid" style={{ marginBottom: 6 }}>
              <div>
                <div className="masterCert__contactLabel">EFFECTIVE DATE</div>
                <div className="masterCert__dateCell">
                  <Calendar value={effectiveDate} onChange={(e) => setEffectiveDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">EXPIRATION DATE</div>
                <div className="masterCert__dateCell">
                  <Calendar value={expirationDate} onChange={(e) => setExpirationDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
                </div>
              </div>
            </div>

            <div className="masterCert__checkboxItem" style={{ marginBottom: 8, marginTop: 8 }}>
              <Checkbox inputId="continuedUntilTerminated" checked={continuedUntilTerminated} onChange={(e) => setContinuedUntilTerminated(e.checked ?? true)} />
              <label htmlFor="continuedUntilTerminated" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>CONTINUED UNTIL TERMINATED IF CHECKED</label>
            </div>

            <div>
              <div className="masterCert__contactLabel">THIS REPLACES PRIOR EVIDENCE DATED:</div>
              <div className="masterCert__dateCell">
                <Calendar value={replacesPriorDate} onChange={(e) => setReplacesPriorDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
              </div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="masterCert__sectionHeader">INSURED:</div>
          <InputText maxLength={100} value={insuredAddress1} onChange={(e) => setInsuredAddress1(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <InputText maxLength={100} value={insuredAddress2} onChange={(e) => setInsuredAddress2(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <InputText maxLength={100} value={insuredAddress3} onChange={(e) => setInsuredAddress3(e.target.value)} placeholder="Suite / Unit" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
          <div className="masterCert__addressGrid">
            <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
            <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
            <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
          </div>
        </div>

        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">PROPERTY INFORMATION</div>
          <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>LOCATION/DESCRIPTION</div>
          <InputTextarea maxLength={2000} value={locationDescription} onChange={(e) => setLocationDescription(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        <div className="masterCert__disclaimer" style={{ marginBottom: 12 }}>
          The policies of insurance listed below have been issued to the insured named above for the policy period indicated. Notwithstanding any requirement, term or condition of any contract or other document with respect to which this evidence of property insurance may be issued or may pertain, the insurance afforded by the policies described herein is subject to all the terms, exclusions, and conditions of such policies. Limits shown may have been reduced by paid claims.
        </div>

        <div className="masterCert__coveragesSection" style={{ borderTop: 'none', paddingTop: 0 }}>
          <div className="masterCert__coveragesTitle">COVERAGE INFORMATION</div>

          {isV2016 && (
            <>
              <div className="masterCert__checkboxItem" style={{ gap: 16, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem', fontWeight: 600 }}>PERILS INSURED</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Checkbox inputId="basic" checked={basic} onChange={(e) => setBasic(e.checked ?? false)} />
                  <label htmlFor="basic" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BASIC</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Checkbox inputId="broad" checked={broad} onChange={(e) => setBroad(e.checked ?? false)} />
                  <label htmlFor="broad" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>BROAD</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Checkbox inputId="special" checked={special} onChange={(e) => setSpecial(e.checked ?? true)} />
                  <label htmlFor="special" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>SPECIAL</label>
                </div>
              </div>
              <div className="masterCert__checkboxItem" style={{ gap: 8, marginBottom: 8 }}>
                <Checkbox inputId="otherCoverageCheck" checked={otherCoverageCheck} onChange={(e) => setOtherCoverageCheck(e.checked ?? false)} />
                <InputText value={otherCoverageText} onChange={(e) => setOtherCoverageText(e.target.value)} className="masterCert__input" style={{ flex: 1 }} />
              </div>
            </>
          )}

          <div className="masterCert__coverageHeader27">
            <div>COVERAGE/PERILS/FORMS</div>
            <div>AMOUNT OF INSURANCE</div>
            <div>DEDUCTIBLE</div>
          </div>

          {coverageRows.map((row, index) => (
            <div key={index} className="masterCert__coverageRow27">
              <InputText
                value={row.coveragePerilsForms}
                onChange={(e) => updateCoverageRow(index, 'coveragePerilsForms', e.target.value)}
                className="masterCert__input masterCert__inputFull"
              />
              <InputText
                value={row.amountOfInsurance}
                onChange={(e) => updateCoverageRow(index, 'amountOfInsurance', e.target.value)}
                placeholder="$"
                className="masterCert__input masterCert__inputFull"
              />
              <InputText
                value={row.deductible}
                onChange={(e) => updateCoverageRow(index, 'deductible', e.target.value)}
                placeholder="$"
                className="masterCert__input masterCert__inputFull"
              />
            </div>
          ))}
        </div>

        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">REMARKS (Including Special Conditions)</div>
          <InputTextarea maxLength={2000} value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className="masterCert__sectionHeader">CANCELLATION</div>
          <div className="masterCert__cancellationText">
            Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
          </div>
        </div>

        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox" style={!aiEnabled ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', minHeight: 140 } : {}}>
            {!aiEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0, color: '#9ca3af' }}>ADDITIONAL INTEREST</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11px', color: '#9ca3af' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '11px' }} />
                  <span>Additional interest can be added here.</span>
                </div>
                <Button
                  label="Enable"
                  icon="pi pi-lock-open"
                  size="small"
                  outlined
                  style={{ height: '28px', fontSize: '11px', padding: '0 14px', borderRadius: '4px', fontWeight: 500 }}
                  onClick={() => setAiEnabled(true)}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>ADDITIONAL INTEREST</div>
                  <Button
                    label="Disable"
                    icon="pi pi-lock"
                    size="small"
                    style={{ height: '22px', fontSize: '10px', padding: '0 8px', borderRadius: '4px', fontWeight: 500 }}
                    onClick={() => { setAiEnabled(false); setAdditionalInterestName(''); setAiMortgagee(false); setAiAdditionalInsured(false); setAiLossPayee(false); setAiLenderLossPayable(false); setAiOtherCheck(false); setAiOtherText(''); setAiLoanNumber(''); }}
                  />
                </div>
                <div className="masterCert__contactLabel" style={{ marginBottom: 4 }}>NAME AND ADDRESS</div>
                <InputTextarea maxLength={2000}
                  value={additionalInterestName}
                  onChange={(e) => setAdditionalInterestName(e.target.value)}
                  rows={5}
                  className="masterCert__descriptionTextarea"
                />
              </>
            )}
          </div>

          <div className="masterCert__cancellationBox">
            {aiEnabled && (isV2016 ? (
              <>
                {/* v2016 layout: ADDITIONAL INSURED | LENDER'S LOSS PAYABLE | LOSS PAYEE */}
                <div className="masterCert__checkboxItem" style={{ gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Checkbox inputId="aiAdditionalInsured" checked={aiAdditionalInsured} onChange={(e) => setAiAdditionalInsured(e.checked ?? false)} />
                    <label htmlFor="aiAdditionalInsured" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>ADDITIONAL INSURED</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Checkbox inputId="aiLenderLossPayable" checked={aiLenderLossPayable} onChange={(e) => setAiLenderLossPayable(e.checked ?? false)} />
                    <label htmlFor="aiLenderLossPayable" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>LENDER'S LOSS PAYABLE</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Checkbox inputId="aiLossPayee" checked={aiLossPayee} onChange={(e) => setAiLossPayee(e.checked ?? false)} />
                    <label htmlFor="aiLossPayee" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>LOSS PAYEE</label>
                  </div>
                </div>
                {/* MORTGAGEE + other checkbox + text */}
                <div className="masterCert__checkboxItem" style={{ gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Checkbox inputId="aiMortgagee" checked={aiMortgagee} onChange={(e) => setAiMortgagee(e.checked ?? false)} />
                    <label htmlFor="aiMortgagee" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>MORTGAGEE</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
                    <Checkbox inputId="aiOtherCheck" checked={aiOtherCheck} onChange={(e) => setAiOtherCheck(e.checked ?? false)} />
                    <InputText value={aiOtherText} onChange={(e) => setAiOtherText(e.target.value)} className="masterCert__input" style={{ width: 160 }} />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Legacy 2009 layout */}
                <div className="masterCert__checkboxItem" style={{ marginBottom: 4 }}>
                  <Checkbox inputId="aiMortgagee" checked={aiMortgagee} onChange={(e) => setAiMortgagee(e.checked ?? false)} />
                  <label htmlFor="aiMortgagee" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>MORTGAGEE</label>
                  <div style={{ marginLeft: 16 }}>
                    <Checkbox inputId="aiAdditionalInsured" checked={aiAdditionalInsured} onChange={(e) => setAiAdditionalInsured(e.checked ?? false)} />
                    <label htmlFor="aiAdditionalInsured" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>ADDITIONAL INSURED</label>
                  </div>
                </div>
                <div className="masterCert__checkboxItem" style={{ marginBottom: 8 }}>
                  <Checkbox inputId="aiLossPayee" checked={aiLossPayee} onChange={(e) => setAiLossPayee(e.checked ?? false)} />
                  <label htmlFor="aiLossPayee" className="masterCert__checkboxItemLabel" style={{ fontSize: '0.8rem' }}>LOSS PAYEE</label>
                  <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Checkbox inputId="aiOtherCheck" checked={aiOtherCheck} onChange={(e) => setAiOtherCheck(e.checked ?? false)} />
                    <InputText value={aiOtherText} onChange={(e) => setAiOtherText(e.target.value)} className="masterCert__input" style={{ width: 160 }} />
                  </div>
                </div>
              </>
            ))}

            {aiEnabled && <div style={{ marginBottom: 12 }}>
              <div className="masterCert__contactLabel">LOAN #</div>
              <InputText maxLength={30} value={aiLoanNumber} onChange={(e) => setAiLoanNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>}

            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button
                label="Create New"
                className="p-button-outlined carrierDialog__secondaryBtn"
                onClick={() => setShowAddSignature(true)}
              />
            </div>
            {signaturePreviewUrl && (
              <div style={{ marginTop: 8 }}>
                <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
              </div>
            )}
          </div>
        </div>
        <div className="masterCert__acordFooter">
          <div>{isV2016 ? 'ACORD 27 (2016/03)' : 'ACORD 27 (2009/12)'}</div>
          <div>&copy; {isV2016 ? 'ACORD CORPORATION 1993-2016' : 'ACORD CORPORATION 1993-2009'}. All rights reserved.</div>
        </div>

        {/* ── ACORD 101 - Additional Remarks Schedule ── */}
        {showAcord101 && (
          <div className="masterCert__acord101">
            <div className="masterCert__acordHeader" style={{ marginTop: 24 }}>
              <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
              <div className="masterCert__acordTitleWrapper">
                <div className="masterCert__acordTitle">ADDITIONAL REMARKS SCHEDULE</div>
              </div>
            </div>
            <div className="masterCert__twoColumnLayout">
              <div>
                <div className="masterCert__sectionHeader">AGENCY</div>
                <InputTextarea maxLength={250} value={acord101Agency} onChange={(e) => setAcord101Agency(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Agency name and address" />
              </div>
              <div>
                <div className="masterCert__sectionHeader">NAMED INSURED</div>
                <InputTextarea maxLength={100} value={acord101NamedInsured} onChange={(e) => setAcord101NamedInsured(e.target.value)} rows={3} className="masterCert__descriptionTextarea" placeholder="Named insured" />
              </div>
            </div>
            <div className="masterCert__descriptionSection">
              <div className="masterCert__descriptionTitle">ADDITIONAL REMARKS</div>
              <InputTextarea maxLength={2000} value={acord101AdditionalRemarks} onChange={(e) => setAcord101AdditionalRemarks(e.target.value)} rows={8} className="masterCert__descriptionTextarea" placeholder="Enter additional remarks..." />
            </div>
            <div className="masterCert__acordFooter">
              <div>ACORD 101 (2008/01)</div>
              <div>&copy; 2008 ACORD CORPORATION. All rights reserved.</div>
            </div>
          </div>
        )}
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
});

export default Acord27Form;
